import React, { useEffect } from 'react';
import { Building, TrendingUp, Users, Home, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';

const ServicesPage = () => {
  const { t, language } = useLanguage();

  // Scroll reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: Home,
      title: t('services.residential.title'),
      description: t('services.residential.description'),
      image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB2aWxsYXxlbnwwfHx8fDE3Njg2MzI0MDN8MA&ixlib=rb-4.1.0&q=85',
      features: language === 'en' 
        ? ['Modern Apartments', 'Luxury Villas', 'Gated Communities', 'Smart Homes']
        : language === 'ar'
        ? ['شقق حديثة', 'فيلات فاخرة', 'مجتمعات مسورة', 'منازل ذكية']
        : ['شووقەی مۆدێرن', 'ڤیلای لوکس', 'کۆمەڵگەی داخراو', 'ماڵی زیرەک']
    },
    {
      icon: TrendingUp,
      title: t('services.infrastructure.title'),
      description: t('services.infrastructure.description'),
      image: 'https://images.unsplash.com/photo-1689574666546-75e1036e55fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxyZXNpZGVudGlhbCUyMGNvbW11bml0eSUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3Njg2ODAwMjF8MA&ixlib=rb-4.1.0&q=85',
      features: language === 'en'
        ? ['Water Systems', 'Electrical Networks', 'Sewage Systems', 'Community Facilities']
        : language === 'ar'
        ? ['أنظمة المياه', 'شبكات كهربائية', 'أنظمة الصرف الصحي', 'مرافق مجتمعية']
        : ['سیستەمی ئاو', 'تۆڕی کارەبا', 'سیستەمی لاوەبردنەوە', 'ئاسانکارییەکانی کۆمەڵگە']
    },
    {
      icon: Building,
      title: t('services.roads.title'),
      description: t('services.roads.description'),
      image: 'https://images.unsplash.com/photo-1615406020658-6c4b805f1f30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzY4NjMyMDUyfDA&ixlib=rb-4.1.0&q=85',
      features: language === 'en'
        ? ['Highway Construction', 'Urban Roads', 'Street Lighting', 'Traffic Management']
        : language === 'ar'
        ? ['بناء الطرق السريعة', 'طرق حضرية', 'إضاءة الشوارع', 'إدارة المرور']
        : ['دروستکردنی ڕێگای باش', 'ڕێگای شارۆچکە', 'ڕووناکی شەقام', 'بەڕێوەبردنی هاتووچۆ']
    },
    {
      icon: Users,
      title: t('services.community.title'),
      description: t('services.community.description'),
      image: 'https://images.unsplash.com/photo-1763783695583-c0703902d318?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxyZXNpZGVudGlhbCUyMGNvbW11bml0eSUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3Njg2ODAwMjF8MA&ixlib=rb-4.1.0&q=85',
      features: language === 'en'
        ? ['Master Planning', 'Green Spaces', 'Recreation Areas', 'Social Infrastructure']
        : language === 'ar'
        ? ['التخطيط الرئيسي', 'مساحات خضراء', 'مناطق الترفيه', 'البنية التحتية الاجتماعية']
        : ['پلانی سەرەکی', 'شوێنی سەوز', 'ناوچەی ڕامیاری', 'ژێرخانی کۆمەڵایەتی']
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black z-10" />
        <img
          src="https://images.unsplash.com/photo-1519662978799-2f05096d3636?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzY4NjMyMDUyfDA&ixlib=rb-4.1.0&q=85"
          alt="Services"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 text-center px-6 max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6 font-light animate-fade-in-up">
            {language === 'en' ? 'WHAT WE OFFER' : language === 'ar' ? 'ما نقدمه' : 'ئەوەی پێشکەش دەکەین'}
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight mb-6 animate-fade-in-up delay-200">
            {t('services.title')}
          </h1>
          <p className="text-lg text-white/70 font-light animate-fade-in-up delay-300">{t('services.subtitle')}</p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-32">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="space-y-32">
            {services.map((service, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
              >
                {/* Text Content */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''} scroll-reveal-left`}>
                  <service.icon className="w-12 h-12 text-white/40 mb-8" />
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8 tracking-tight">
                    {service.title}
                  </h2>
                  <p className="text-lg text-white/60 font-light leading-relaxed mb-10">
                    {service.description}
                  </p>
                  <div className="space-y-4">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-4 group">
                        <div className="w-8 h-px bg-white/30 group-hover:w-12 group-hover:bg-white transition-all duration-300" />
                        <span className="text-white/70 font-light">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''} scroll-reveal-right`}>
                  <div className="relative aspect-[4/5] overflow-hidden image-zoom-container">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover image-zoom"
                    />
                    {/* Decorative corner */}
                    <div className={`absolute w-24 h-24 border border-white/20 ${
                      index % 2 === 0 ? '-bottom-4 -right-4' : '-bottom-4 -left-4'
                    }`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-black/40 mb-6 font-light scroll-reveal">
            {language === 'en' ? 'START YOUR PROJECT' : language === 'ar' ? 'ابدأ مشروعك' : 'پڕۆژەکەت دەست پێ بکە'}
          </p>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-8 tracking-tight scroll-reveal">
            {language === 'en' ? 'Ready to Start Your Project?' : language === 'ar' ? 'هل أنت مستعد لبدء مشروعك؟' : 'ئامادەیت بۆ دەستپێکردنی پڕۆژەکەت؟'}
          </h2>
          <p className="text-lg text-black/60 mb-12 font-light max-w-2xl mx-auto scroll-reveal">
            {language === 'en' ? "Let's discuss how we can bring your residential development vision to life" : language === 'ar' ? 'دعنا نناقش كيف يمكننا تحقيق رؤيتك للتطوير السكني' : 'با باس بکەین چۆن دەتوانین بینینی گەشەپێدانی نیشتەجێبوونەکەت بهێنینە ژیان'}
          </p>
          <Link
            to="/contact"
            className="btn-luxury inline-flex items-center gap-3 px-12 py-5 bg-black text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-black/90 scroll-reveal"
          >
            {language === 'en' ? 'Contact Us Today' : language === 'ar' ? 'تواصل معنا اليوم' : 'ئەمڕۆ پەیوەندیمان پێوە بکە'}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
